/**
 * Sign-in methods of a Music24 user (Settings -> Account -> Sign-in methods).
 *
 * One rule for music24-api, the web app and the native apps. The file has no imports.
 *
 * A user signs in with:
 * - the email code, sent to the primary address of the account;
 * - a connected social identity (`UserSocialIdentityModel`): Apple or Google.
 *
 * The server owns the decision. The clients use these functions only to show the state.
 */

/** The social providers that a user can connect. The order is the order on screen. */
export const SIGN_IN_PROVIDERS = ['apple', 'google'] as const;
export type SignInProvider = (typeof SIGN_IN_PROVIDERS)[number];

/** Apple's "Hide My Email" relay domain. */
const APPLE_PRIVATE_RELAY_DOMAIN = '@privaterelay.appleid.com';

/** True for an Apple "Hide My Email" relay address. */
export function isApplePrivateRelayEmail(email: string): boolean {
  return email.trim().toLowerCase().endsWith(APPLE_PRIVATE_RELAY_DOMAIN);
}

/**
 * True when the email code is a way to sign in for this primary address.
 *
 * A relay address does not count: Apple forwards mail to it only from a sender domain that is
 * registered in the Apple portal, so the code can fail to arrive. An empty address does not count.
 */
export function isEmailCodeSignInUsable(primaryEmail: string): boolean {
  const email = primaryEmail.trim();
  return email !== '' && email.includes('@') && !isApplePrivateRelayEmail(email);
}

/**
 * True when the user keeps at least one way to sign in after the disconnect of one identity.
 * `otherIdentityCount` counts the connected identities that stay.
 */
export function canDisconnectSignInMethod({
  primaryEmail,
  otherIdentityCount,
}: {
  primaryEmail: string;
  otherIdentityCount: number;
}): boolean {
  return otherIdentityCount > 0 || isEmailCodeSignInUsable(primaryEmail);
}

/**
 * The refusals of the sign-in methods routes. The client reads `error.data.errorType`.
 * - `M24_SIGNIN_LINK_STATE_INVALID`: the link state is unknown, used, expired, of another user or
 *   of another provider. Start the connect again.
 * - `M24_SIGNIN_PROOF_INVALID`: the provider token or code did not pass the check.
 * - `M24_SIGNIN_IDENTITY_TAKEN`: this Apple or Google account already signs in to another Music24
 *   account. Nothing is merged.
 * - `M24_SIGNIN_PROVIDER_ALREADY_CONNECTED`: the user already has another account of this provider.
 * - `M24_SIGNIN_PROVIDER_NOT_CONFIGURED`: the server has no keys for this provider.
 * - `M24_SIGNIN_LAST_METHOD`: a disconnect would leave no way to sign in.
 * - `M24_SIGNIN_METHOD_NOT_FOUND`: no such identity of this user.
 */
export const SIGN_IN_METHOD_ERRORS = [
  'M24_SIGNIN_LINK_STATE_INVALID',
  'M24_SIGNIN_PROOF_INVALID',
  'M24_SIGNIN_IDENTITY_TAKEN',
  'M24_SIGNIN_PROVIDER_ALREADY_CONNECTED',
  'M24_SIGNIN_PROVIDER_NOT_CONFIGURED',
  'M24_SIGNIN_LAST_METHOD',
  'M24_SIGNIN_METHOD_NOT_FOUND',
] as const;
export type SignInMethodErrorCode = (typeof SIGN_IN_METHOD_ERRORS)[number];

export function isSignInMethodErrorCode(value: unknown): value is SignInMethodErrorCode {
  return typeof value === 'string' && (SIGN_IN_METHOD_ERRORS as readonly string[]).includes(value);
}

/** One connected identity, as `signInMethods.list` answers it. */
export interface SignInIdentity {
  id: number;
  provider: SignInProvider;
  /** The address that the provider gave. For Apple it can be a relay address. */
  email: string;
  isPrivateRelay: boolean;
  connectedAt: string;
  /** False when this identity is the last way to sign in. */
  canDisconnect: boolean;
}

export interface SignInMethodsAnswer {
  primaryEmail: string;
  /** The email code reaches the primary address (not a relay address). */
  emailCode: { usable: boolean };
  identities: SignInIdentity[];
  /** The providers that the server can connect now (keys present). */
  availableProviders: SignInProvider[];
}
